// src/components/ReferralCreditHistory.jsx

import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Flex,
  Input,
  Button,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import useBusinessGuard from "../hooks/useBusinessGuard";

/**
 * ReferralCreditHistory
 *
 * Shows referral bonus history using UUID identifiers.
 *
 * Earnings total reflects only referrals earned
 * by the current business acting as the referrer.
 */

export default function ReferralCreditHistory() {
  const { profile, loading: authLoading } =
    useBusinessGuard();

  const businessUuid =
    profile?.business_uuid;

  const [audit, setAudit] = useState([]);
  const [loading, setLoading] =
    useState(true);
  const [totalEarned, setTotalEarned] =
    useState(0);

  const [filterEmail, setFilterEmail] =
    useState("");

  const [filterDate, setFilterDate] =
    useState("");

  useEffect(() => {
    if (
      authLoading ||
      !businessUuid
    )
      return;

    const fetchAudit =
      async () => {
        setLoading(true);

        try {
          const {
            data,
            error,
          } = await supabase
            .from("referral_audit")
            .select(`
              id,
              referred_email,
              referrer_uuid,
              referred_business_uuid,
              triggered_at,
              bonus_amount,
              currency,
              source
            `)
            .or(
              `referrer_uuid.eq.${businessUuid},referred_business_uuid.eq.${businessUuid}`
            )
            .order(
              "triggered_at",
              {
                ascending: false,
              }
            );

          if (error)
            throw error;

          const rows =
            data || [];

          setAudit(rows);

          /*
           * Only count earnings where
           * this business was the referrer.
           */
          const earned =
            rows.reduce(
              (
                sum,
                entry
              ) => {
                const isReferrer =
                  entry.referrer_uuid ===
                  businessUuid;

                if (
                  !isReferrer
                )
                  return sum;

                return (
                  sum +
                  Number(
                    entry.bonus_amount ||
                      0
                  )
                );
              },
              0
            );

          setTotalEarned(
            earned
          );
        } catch (
          err
        ) {
          console.error(
            "[ReferralCreditHistory] Failed to fetch referral audit:",
            err
          );
        } finally {
          setLoading(
            false
          );
        }
      };

    fetchAudit();
  }, [
    authLoading,
    businessUuid,
  ]);

  const filteredAudit =
    audit.filter(
      (entry) => {
        const emailMatch =
          !filterEmail ||
          (
            entry.referred_email ||
            ""
          )
            .toLowerCase()
            .includes(
              filterEmail.toLowerCase()
            );

        const dateMatch =
          !filterDate ||
          (
            entry.triggered_at ||
            ""
          ).startsWith(
            filterDate
          );

        return (
          emailMatch &&
          dateMatch
        );
      }
    );

  const exportCSV = (
    rows
  ) => {
    const headers = [
      "Date",
      "Role",
      "Referred Email",
      "Amount",
      "Currency",
      "Source",
    ];

    const csvRows =
      rows.map(
        (entry) => {
          const isReferrer =
            entry.referrer_uuid ===
            businessUuid;

          return [
            entry.triggered_at
              ? new Date(
                  entry.triggered_at
                ).toLocaleDateString()
              : "",
            isReferrer
              ? "Referrer"
              : "Referred",
            entry.referred_email ||
              "",
            Number(
              entry.bonus_amount ||
                0
            ).toFixed(2),
            entry.currency ||
              "GBP",
            entry.source ||
              "",
          ];
        }
      );

    const csv = [
      headers,
      ...csvRows,
    ]
      .map((r) =>
        r.join(",")
      )
      .join("\n");

    const blob =
      new Blob([csv], {
        type: "text/csv",
      });

    const url =
      URL.createObjectURL(
        blob
      );

    const a =
      document.createElement(
        "a"
      );

    a.href = url;
    a.download =
      "referral_credit_history.csv";

    a.click();

    URL.revokeObjectURL(
      url
    );
  };

  if (
    authLoading ||
    !businessUuid
  )
    return null;

  return (
    <Box
      p={4}
      bg="gray.50"
      borderRadius="md"
      mt={10}
    >
      <Text
        fontWeight="bold"
        mb={2}
      >
        💸 Referral Credit
        History
      </Text>

      <Text
        fontSize="lg"
        fontWeight="bold"
        mb={4}
        color="green.600"
      >
        💰 Total Referral
        Earnings: £
        {totalEarned.toFixed(
          2
        )}
      </Text>

      <Flex
        gap={4}
        mb={4}
        flexWrap="wrap"
      >
        <Input
          placeholder="Filter by referred email"
          value={
            filterEmail
          }
          onChange={(
            e
          ) =>
            setFilterEmail(
              e.target
                .value
            )
          }
          width="220px"
        />

        <Input
          type="date"
          value={
            filterDate
          }
          onChange={(
            e
          ) =>
            setFilterDate(
              e.target
                .value
            )
          }
          width="180px"
        />

        <Button
          size="sm"
          onClick={() =>
            exportCSV(
              filteredAudit
            )
          }
        >
          Export CSV
        </Button>
      </Flex>

      {loading ? (
        <Flex
          align="center"
          justify="center"
          minH="100px"
        >
          <Spinner />
        </Flex>
      ) : filteredAudit.length ===
        0 ? (
        <Text>
          No referral
          activity found.
        </Text>
      ) : (
        <Table
          size="sm"
          variant="simple"
        >
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Role</Th>
              <Th>
                Referred
                Email
              </Th>
              <Th>
                Amount
              </Th>
              <Th>
                Currency
              </Th>
              <Th>
                Source
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {filteredAudit.map(
              (
                entry
              ) => {
                const isReferrer =
                  entry.referrer_uuid ===
                  businessUuid;

                const amount =
                  Number(
                    entry.bonus_amount ||
                      0
                  );

                return (
                  <Tr
                    key={
                      entry.id
                    }
                  >
                    <Td>
                      {entry.triggered_at
                        ? new Date(
                            entry.triggered_at
                          ).toLocaleDateString()
                        : "—"}
                    </Td>

                    <Td>
                      {isReferrer
                        ? "Referrer"
                        : "Referred"}
                    </Td>

                    <Td>
                      {entry.referred_email ||
                        "—"}
                    </Td>

                    <Td
                      color={
                        amount >=
                        0
                          ? "green.600"
                          : "red.600"
                      }
                      fontWeight="bold"
                    >
                      £
                      {amount.toFixed(
                        2
                      )}
                    </Td>

                    <Td>
                      {entry.currency ||
                        "GBP"}
                    </Td>

                    <Td>
                      {entry.source ||
                        "—"}
                    </Td>
                  </Tr>
                );
              }
            )}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}